/**
 * 🔐 GOVERNANCE PERMISSIONS — Excelência Module
 *
 * Controle de acesso para ações de governança de processos.
 *
 * Roles com permissão de governança:
 *   - admin / consultor_master / consultant
 *   - gestor / manager / governance_manager
 *
 * Ações restritas (requerem governança):
 *   Publicar · Definir como padrão · Arquivar · Excluir
 *
 * Ações livres (qualquer usuário autenticado):
 *   Visualizar · Criar rascunho · Editar rascunho · Clonar · Versionar · Ver uso
 */

export type GovernanceRole = 'admin' | 'gestor' | 'editor' | 'leitor' | 'governance_manager' | string;
export type GovernanceAction = 'create' | 'edit' | 'publish' | 'archive' | 'setStandard' | 'delete' | 'link';

const ROLE_PERMISSIONS: Record<'admin' | 'gestor' | 'editor' | 'leitor', GovernanceAction[]> = {
  admin: ['create', 'edit', 'publish', 'archive', 'setStandard', 'delete', 'link'],
  gestor: ['create', 'edit', 'publish', 'archive', 'setStandard', 'link'],
  editor: ['create', 'edit', 'link'],
  leitor: [],
};

function normalizeRole(role?: GovernanceRole | null): 'admin' | 'gestor' | 'editor' | 'leitor' {
  if (!role) return 'leitor';
  const r = role.toLowerCase();
  if (r === 'admin' || r === 'consultor_master' || r === 'consultant_master' || r === 'consultant') return 'admin';
  if (r === 'gestor' || r === 'manager' || r === 'governance_manager') return 'gestor';
  if (r === 'editor') return 'editor';
  if (r === 'operator' || r === 'operador' || r === 'usuario' || r === 'leitor') return 'leitor';
  return 'leitor';
}

/** Roles que possuem permissão para ações de governança críticas */
const GOVERNANCE_ROLES: ReadonlyArray<string> = ['admin', 'gestor', 'governance_manager'];

/**
 * Verifica se o role do usuário possui permissão para ações de governança
 * críticas (Publicar, Definir como padrão, Arquivar, Excluir).
 */
export function canGovernBlueprints(role?: string | null): boolean {
  if (!role) return false;
  const normalized = normalizeRole(role);
  return GOVERNANCE_ROLES.includes(normalized);
}

/**
 * Verifica permissão granular.
 */
export function canPerformGovernanceAction(action: GovernanceAction, role?: GovernanceRole | null): boolean {
  const normalized = normalizeRole(role);
  const allowed = ROLE_PERMISSIONS[normalized] ?? [];
  return allowed.includes(action);
}

/**
 * Garante permissão granular, lançando erro descritivo se não autorizado.
 */
export function assertGovernancePermission(action: GovernanceAction, role?: GovernanceRole | null): void {
  if (!canPerformGovernanceAction(action, role)) {
    throw new Error('Permissão negada para esta ação de governança. Contate um gestor ou admin.');
  }
}
